import React from 'react';
import { toast } from 'sonner';
import { markOptimisticConfirmed } from '@/components/chat/chatText';
import {
  adoptSend,
  getPending,
  hasPending,
  isTransientSendError,
  markScreenActive,
  subscribe,
} from '@/utils/chatOutbox';

// Teks pemberitahuan untuk pesan antrean yang gagal permanen (dipakai juga oleh ConnectivityLayer).
export function failedOutboxMessage(evt) {
  const item = evt.item || {};
  const text = item.payload?.content ?? item.payload?.message ?? item.payload?.message_text ?? '';
  if (evt.reason === 'expired') return 'Pesan tidak terkirim lebih dari 3 hari dan dibatalkan.';
  const detail = evt.detail ? `: ${evt.detail}` : '';
  return `Pesan gagal dikirim${evt.status ? ` (${evt.status})` : ''}${detail}${text ? `. Isi: "${String(text).slice(0, 200)}"` : ''}`;
}

/**
 * Sambungan layar chat ke antrean kirim (Fase 4A).
 *
 * chatKey   : pengenal unik chat (mis. "nakes:CHAT_ID")
 * messages  : state pesan layar (untuk mencatat id yang sudah dikenal)
 * setMessages, refetch : dari layar
 * onFailed(evt) : opsional, dipanggil saat pesan antrean ditolak permanen
 *
 * Mengembalikan:
 *   shouldQueueFirst() : true kalau sudah ada pesan menunggu di chat ini
 *                        (pesan baru harus ikut antre supaya urutan terjaga)
 *   queue(spec, opts)  : masukkan ke antrean; mengembalikan true kalau berhasil
 *   snapshotIds() : id pesan yang sudah ada saat ini; ambil SAAT pesan dikirim (bukan saat diantre)
 *   tryQueueOnError(err, spec) : true kalau error karena sinyal dan pesan
 *                        berhasil diantrekan (layar tidak perlu menghapus pesan)
 */
export function useChatOutbox({ chatKey, messages, setMessages, refetch, onFailed }) {
  const messagesRef = React.useRef(messages);
  messagesRef.current = messages;
  const refetchRef = React.useRef(refetch);
  refetchRef.current = refetch;
  const onFailedRef = React.useRef(onFailed);
  onFailedRef.current = onFailed;

  // Tampilkan kembali pesan yang masih menunggu (mis. setelah halaman dibuka ulang).
  React.useEffect(() => {
    if (!chatKey) return undefined;
    const pending = getPending(chatKey);
    if (pending.length) {
      setMessages((prev) => {
        const have = new Set(prev.map((m) => m.message_id));
        const add = pending.map((p) => p.tempMessage).filter((t) => t && !have.has(t.message_id));
        return add.length ? [...prev, ...add] : prev;
      });
    }
    const refetchTimers = [];
    const release = markScreenActive(chatKey);
    const off = subscribe((evt) => {
      const item = evt.item;
      if (!item || item.chatKey !== chatKey) return;
      if (evt.type === 'queued') {
        setMessages((prev) => prev.map((m) => (m.message_id === item.id ? { ...m, _outbox: true } : m)));
      } else if (evt.type === 'delivered') {
        markOptimisticConfirmed(setMessages, item.id);
        // Jeda singkat: refetch yang dimulai di milidetik yang sama dengan konfirmasi membuat
        // pesan sementara bertahan sampai polling berikutnya (terlihat dobel).
        refetchTimers.push(setTimeout(() => {
          Promise.resolve(refetchRef.current && refetchRef.current()).catch(() => {});
        }, 60));
      } else if (evt.type === 'failed') {
        setMessages((prev) => prev.filter((m) => m.message_id !== item.id));
        toast.error(failedOutboxMessage(evt));
        try { onFailedRef.current && onFailedRef.current(evt); } catch { /* abaikan */ }
      }
    });
    return () => {
      off();
      release();
      refetchTimers.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatKey]);

  const knownIds = () =>
    (messagesRef.current || []).filter((m) => m && !m._optimistic && m.message_id).map((m) => m.message_id);

  const snapshotIds = React.useCallback(() => knownIds(), []);

  const queue = React.useCallback(
    (spec, { maybeLanded = true } = {}) =>
      !!adoptSend({ ...spec, chatKey, knownIds: spec.knownIds || knownIds(), maybeLanded }),
    [chatKey]
  );

  const shouldQueueFirst = React.useCallback(() => !!chatKey && hasPending(chatKey), [chatKey]);

  const tryQueueOnError = React.useCallback(
    (err, spec) => {
      if (!isTransientSendError(err)) return false;
      const ok = queue(spec, { maybeLanded: true });
      if (ok) toast.info('Sinyal sedang lemah. Pesan disimpan dan dikirim otomatis saat sinyal membaik.');
      return ok;
    },
    [queue]
  );

  return { shouldQueueFirst, queue, tryQueueOnError, snapshotIds };
}
